// POS multi-draft tab bar: horizontal scrollable strip of draft tabs + new order button.
import { useRef, useEffect } from "react";
import { Plus, X } from "lucide-react";
import type { Order } from "./use-pos-cart";

interface PosTabBarProps {
  drafts: Order[];
  activeOrderId?: string;
  onSelect: (orderId: string) => void;
  onClose: (orderId: string) => void;
  onNew: () => void;
  loading?: boolean;
}

export function PosTabBar({
  drafts,
  activeOrderId,
  onSelect,
  onClose,
  onNew,
  loading,
}: PosTabBarProps) {
  const activeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [activeOrderId]);

  // Ensure the active order always appears in the tab list.
  const tabs = activeOrderId && !drafts.some((d) => d.id === activeOrderId)
    ? [{ id: activeOrderId, code: "Đơn hiện tại", items: [] as unknown[] } as Order, ...drafts]
    : drafts;

  return (
    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide shrink-0 min-w-0">
      {tabs.map((draft) => {
        const isActive = draft.id === activeOrderId;
        const itemCount = draft.items?.length ?? 0;
        return (
          <div
            key={draft.id}
            ref={isActive ? activeRef : undefined}
            className={[
              "group h-8 shrink-0 flex items-center rounded-md text-xs font-medium whitespace-nowrap transition-colors border",
              isActive
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground hover:bg-muted border-border",
            ].join(" ")}
          >
            <button
              onClick={() => onSelect(draft.id)}
              className="h-full pl-3 pr-1"
            >
              {draft.code}
              {itemCount > 0 && (
                <span className={`ml-1 ${isActive ? "opacity-80" : "opacity-60"}`}>({itemCount})</span>
              )}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onClose(draft.id); }}
              className={[
                "h-5 w-5 mr-1 flex items-center justify-center rounded-sm transition-colors",
                isActive
                  ? "opacity-60 hover:opacity-100 hover:bg-primary-foreground/20"
                  : "opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:bg-muted-foreground/20",
              ].join(" ")}
              title="Đóng đơn nháp"
            >
              <X size={10} />
            </button>
          </div>
        );
      })}

      <button
        onClick={onNew}
        disabled={loading}
        className="h-8 shrink-0 flex items-center gap-1 rounded-md border border-dashed border-muted-foreground/40 px-3 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
        title="Đơn hàng mới (F11)"
      >
        <Plus size={12} />
        <span>Đơn mới</span>
      </button>
    </div>
  );
}
